import logoLDA from "@/assets/logo-horizontal.png";
import Modal from "./ui/modal";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { PrivacyContent } from "./legal/PrivacyContent";
import { TermsContent } from "./legal/TermsContent";
import { Facebook, Instagram, Twitter, Youtube, Music } from "lucide-react";
import { SOCIAL_LINKS, COMPANY_INFO } from "@/config/constants";

export const Footer = () => {
  const [open, setOpen] = useState<null | "terms" | "privacy">(null);
  const location = useLocation();
  const is8PilaresRoute = location.pathname === "/8pilares" || location.pathname === "/metodolda";

  return (
    <footer className="relative py-12 border-t border-primary/10">
      <div className="container px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo and tagline */}
            <div className="text-center md:text-left">
              <img src={logoLDA} alt="LDA" className="h-24 mx-auto md:mx-0 mb-4" />
              <p className="text-muted-foreground text-sm">
                Supere o Vício, Reconquiste Sua Liberdade
              </p>
            </div>

            {/* Links */}
            <div className="text-center">
              <h4 className="font-semibold mb-4">Links Importantes</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <button
                    type="button"
                    onClick={() => setOpen("terms")}
                    className="hover:text-accent transition-colors"
                  >
                    Termos de Uso
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setOpen("privacy")}
                    className="hover:text-accent transition-colors"
                  >
                    Política de Privacidade
                  </button>
                </li>
                {!is8PilaresRoute && (
                  <li>
                    <a href="#faq" className="hover:text-accent transition-colors">
                      Perguntas Frequentes
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Modais */}
            <Modal
              open={open === "terms"}
              onClose={() => setOpen(null)}
              title="Termos de Uso — LDA"
            >
              <TermsContent />
            </Modal>

            <Modal
              open={open === "privacy"}
              onClose={() => setOpen(null)}
              title="Política de Privacidade — LDA"
            >
              <PrivacyContent />
            </Modal>

            {/* Contact */}
            <div className="text-center md:text-right">
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>{COMPANY_INFO.email}</li>
                <li>Telefone/WhatsApp: (11) 5304-1050</li>
                <li className="mt-3">
                  <strong>Endereço:</strong><br />
                  R. Lira dos Verdes Anos, nº 129<br />
                  Jardim São Savério<br />
                  CEP: 04.194-250<br />
                  São Paulo - SP
                </li>
              </ul>
            </div>
          </div>

          {/* Redes Sociais */}
          <div className="grid md:grid-cols-2 gap-8 py-8 border-t border-primary/10">
            {/* Marlon */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold mb-4 text-cyan-400">Redes Sociais - Marlon</h4>
              <div className="flex justify-center md:justify-start gap-4">
                <a
                  href={SOCIAL_LINKS.marlon.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Instagram - Marlon"
                >
                  <Instagram className="w-5 h-5 text-muted-foreground group-hover:text-cyan-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.marlon.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="TikTok - Marlon"
                >
                  <Music className="w-5 h-5 text-muted-foreground group-hover:text-cyan-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.marlon.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Facebook - Marlon"
                >
                  <Facebook className="w-5 h-5 text-muted-foreground group-hover:text-cyan-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.marlon.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Twitter - Marlon"
                >
                  <Twitter className="w-5 h-5 text-muted-foreground group-hover:text-cyan-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.marlon.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="YouTube - Marlon"
                >
                  <Youtube className="w-5 h-5 text-muted-foreground group-hover:text-cyan-400" />
                </a>
              </div>
            </div>

            {/* Camilla */}
            <div className="text-center md:text-right">
              <h4 className="font-semibold mb-4 text-pink-400">Redes Sociais - Camilla</h4>
              <div className="flex justify-center md:justify-end gap-4">
                <a
                  href={SOCIAL_LINKS.camilla.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Instagram - Camilla"
                >
                  <Instagram className="w-5 h-5 text-muted-foreground group-hover:text-pink-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.camilla.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="TikTok - Camilla"
                >
                  <Music className="w-5 h-5 text-muted-foreground group-hover:text-pink-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.camilla.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Facebook - Camilla"
                >
                  <Facebook className="w-5 h-5 text-muted-foreground group-hover:text-pink-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.camilla.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="Twitter - Camilla"
                >
                  <Twitter className="w-5 h-5 text-muted-foreground group-hover:text-pink-400" />
                </a>
                <a
                  href={SOCIAL_LINKS.camilla.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors group"
                  aria-label="YouTube - Camilla"
                >
                  <Youtube className="w-5 h-5 text-muted-foreground group-hover:text-pink-400" />
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center pt-8 border-t border-primary/10">
            <p className="text-sm text-muted-foreground mb-2">
              <strong>53.820.194 MARLON GUSTAVO DA SILVA RIBEIRO</strong>
            </p>
            <p className="text-sm text-muted-foreground mb-3">
              CNPJ: 53.820.194/0001-38
            </p>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} LDA - Libertação das Apostas. Todos os direitos reservados.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Este site não é afiliado ao Facebook ou a qualquer entidade do Facebook.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
